import { useNavigate } from '@tanstack/react-router';
import { useAtomValue } from 'jotai';
import { Loader2, Mail, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

import logo from '@/assets/OKYC logo.webp';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { clearAuthState, userAtom } from '@/stores/auth.store';

import { useResendVerificationEmail } from '../hooks/use-resend-verification-email';
import { useVerifyEmail } from '../hooks/use-verify-email';

interface VerifyEmailViewProps {
  token?: string;
}

export function VerifyEmailView({ token }: VerifyEmailViewProps) {
  const user = useAtomValue(userAtom);
  const navigate = useNavigate();
  const { mutate: verify, isPending: isVerifying, isError, error } = useVerifyEmail();
  const { mutate: resend, isPending: isResending } = useResendVerificationEmail();
  const [hasAttemptedVerify, setHasAttemptedVerify] = useState(false);

  useEffect(() => {
    if (token && !hasAttemptedVerify) {
      setHasAttemptedVerify(true);
      // Strip the token from the URL before doing ANY work. Without this, the
      // token leaks via the Referer header to every subresource (analytics,
      // fonts, images, Sentry) and is retained in browser history and any
      // upstream access logs. Use replaceState so back-nav doesn't resurrect it.
      if (typeof window !== 'undefined') {
        window.history.replaceState(null, '', '/verify-email');
      }
      verify(token);
    }
  }, [token, verify, hasAttemptedVerify]);

  async function handleSwitchAccount() {
    clearAuthState();
    navigate({ to: '/login' });
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-[#F5F7F9]">
      <Card className="w-[390px] rounded-[5px] border border-[#D4DAE0] bg-white p-6">
        <CardHeader className="space-y-6 p-0 text-center">
          <img src={logo} alt="OKYC Logo" className="mx-auto h-16 w-auto" />
          <h1 className="text-[20px] font-semibold leading-[120%] text-[#051923]">
            Verify your email
          </h1>
        </CardHeader>

        <CardContent className="mt-6 space-y-6 p-0 text-center">
          {token && isVerifying && (
            <div className="flex flex-col items-center space-y-3">
              <Loader2 className="h-8 w-8 animate-spin text-[#131313]" />
              <p className="text-[16px] text-[#051923]">Verifying your email…</p>
            </div>
          )}

          {token && !isVerifying && isError && (
            <div className="flex flex-col items-center space-y-3">
              <XCircle className="h-10 w-10 text-red-500" />
              <p className="text-[16px] text-[#051923]">
                {error?.message ||
                  'Invalid or expired verification link. Please request a new one.'}
              </p>
              <Button
                onClick={() => resend()}
                disabled={isResending}
                className="h-[45px] w-full rounded-[5px] bg-[#131313] text-[18px] font-medium text-white"
              >
                {isResending ? 'Sending…' : 'Resend verification email'}
              </Button>
            </div>
          )}

          {!token && (
            <div className="flex flex-col items-center space-y-4">
              <Mail className="h-10 w-10 text-[#131313]" />
              <p className="text-[16px] text-[#051923]">
                {user?.email ? (
                  <>
                    We sent a verification link to{' '}
                    <span className="font-semibold">{user.email}</span>. Click the link in the email
                    to activate your account.
                  </>
                ) : (
                  <>Check your inbox for a verification link to activate your account.</>
                )}
              </p>
              <Button
                onClick={() => resend()}
                disabled={isResending}
                className="h-[45px] w-full rounded-[5px] bg-[#131313] text-[18px] font-medium text-white"
              >
                {isResending ? 'Sending…' : 'Resend verification email'}
              </Button>
              <button
                type="button"
                onClick={handleSwitchAccount}
                className="text-[14px] text-[#051923] underline"
              >
                Use a different email
              </button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
